import { Injectable } from "@angular/core";
import { ProductType } from "@obg/core.config";
import { StatusCodes } from "@obg/ngx.global";
import { find } from "lodash-es";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { AppError, AppErrorType } from "../app-error/app-error.model";
import { CoreHttpClient } from "../http-client/http-client.service";

import { WalletConfigService } from "./wallet.config";
import {
	AamsBalance,
	AamsBalances,
	AamsBalancesPayload,
	AamsBonusContextBalance,
	Balance,
	Balances,
	BetWinSummary,
	BonusContext,
	BonusContextBalance,
	Transaction,
	TransactionDetail,
	TransactionFilter,
	TransactionTotals,
	TransactionTotalsFilter,
	WithdrawalRestrictions,
} from "./wallet.model";
/**
 * @deprecated [fabric-deprecated] - Use wallet-service from app-wallet instead
 */
@Injectable({
	providedIn: "root",
})
export class WalletService {
	constructor(
		private httpClient: CoreHttpClient,
		private config: WalletConfigService
	) {}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetWalletBalance from instead
	 */
	getBalance(): Observable<Balances | AamsBalancesPayload> {
		return this.httpClient.get<Balances | AamsBalancesPayload>({
			url: this.config.getBalanceEndpoint(),
			suppressErrorLogging: [StatusCodes.Unauthorized],
		});
	}

	getBalanceByContext(context: string): Observable<BonusContextBalance> {
		return this.httpClient.get({
			url: this.config.getBalanceContextEndpoint(),
			searchParams: { context },
		});
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetBetWinSummary instead
	 */
	getBetWinSummary(minutes: number): Observable<BetWinSummary> {
		return this.httpClient.get({
			url: this.config.getBetWinSummaryEndpoint(),
			searchParams: { minutes },
		});
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetTransaction instead
	 */
	getTransactions(filter: TransactionFilter): Observable<Transaction> {
		return this.httpClient
			.get<Transaction>({
				url: this.config.getTransactionsEndpoint(),
				searchParams: filter,
				suppressErrorLogging: [StatusCodes.NotFound],
			})
			.pipe(
				catchError((error: AppError) => {
					if (error.type === AppErrorType.NotFound) {
						return of({
							transactions: [],
							hasMoreResults: false,
						});
					}
					return throwError(() => error);
				})
			);
	}

	getTransaction(externalTransactionId: string): Observable<TransactionDetail> {
		return this.httpClient.get({
			url: this.config.getTransactionEndpoint(),
			searchParams: { externalTransactionId },
		});
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetTransactionTotals instead
	 */
	getTransactionTotals(filter: TransactionTotalsFilter): Observable<TransactionTotals> {
		return this.httpClient
			.get({
				url: this.config.getTransactionTotalsEndpoint(),
				searchParams: filter,
			})
			.pipe(
				catchError((error: AppError) => {
					if (error.type === AppErrorType.NotFound) {
						return of({});
					}
					// todo: OBGB-6215 change `any` to `never` and fix type errors as that is the correct return type
					return throwError(error) as Observable<any>;
				})
			);
	}

	getBalanceByProduct(productType: ProductType, balances: AamsBalances | Balances): Balance | AamsBalance {
		const aamsBalances = balances as AamsBalances;

		if (aamsBalances?.balance && "availableToWithdraw" in aamsBalances?.balance) {
			return this.getAamsBalanceByProduct(productType, aamsBalances);
		}

		return this.getDefaultBalanceByProduct(productType, balances as Balances);
	}

	getBonusContext(productType: ProductType): BonusContext {
		return this.config.getBonusContextByProduct()[productType];
	}

	getWithdrawalRestriction(): Observable<WithdrawalRestrictions> {
		return this.httpClient.get({
			url: this.config.getWithdrawalRestrictionEndpoint(),
		});
	}

	private getDefaultBalanceByProduct(productType: ProductType, balances: Balances): Balance {
		let productBalance: BonusContextBalance | undefined;
		const contextName = this.getBonusContext(productType);

		if (this.config.getBalanceByProductEnabled() && contextName) {
			productBalance = find(balances.bonusContextBalances, { contextName });
		}
		if (!productBalance) {
			return balances.balance;
		}

		const productBalances = [
			productBalance.bonusAmount,
			productBalance.lockedAmount,
			balances.balance.withdrawableAmount,
		].filter((value) => !!value);

		const totalAmount = this.roundUpToCents(
			productBalances.reduce((accumulator: number, value: number) => accumulator + value, 0)
		) as number;

		return {
			totalAmount,
			withdrawableAmount: balances.balance.withdrawableAmount,
			lockedAmount: productBalance.lockedAmount,
			bonusAmount: productBalance.bonusAmount,
			currencyCode: productBalance.currencyCode,
		};
	}

	private roundUpToCents(amount: number | undefined): number {
		if (!amount) {
			return 0;
		}

		return Math.round(amount * 100) / 100;
	}

	private getAamsBalanceByProduct(productType: ProductType, balances: AamsBalances): AamsBalance {
		let aamsProductBalance: AamsBonusContextBalance | undefined;
		const contextName = this.getBonusContext(productType);

		if (this.config.getBalanceByProductEnabled() && contextName) {
			aamsProductBalance = find(balances.bonusContextBalances, { bonusContext: contextName });
		}
		if (!aamsProductBalance) {
			return balances.balance;
		}
		const productBalances = [
			aamsProductBalance.realBonusMoney.amount,
			aamsProductBalance.lockedMoney.amount,
			aamsProductBalance.specialBonusMoney.amount,
		].filter((value) => !!value);

		const totalAmount = this.roundUpToCents(
			productBalances.reduce((accumulator: number, value: number) => accumulator + value, 0)
		) as number;

		return {
			totalAmount,
			availableToWithdraw: balances.balance.availableToWithdraw,
			lockedMoney: aamsProductBalance.lockedMoney,
			realBonusMoney: aamsProductBalance.realBonusMoney,
			specialBonusMoney: aamsProductBalance.specialBonusMoney,
		};
	}
}
